import React from "react";
import { Link } from "react-router-dom";
import { FiClock } from "react-icons/fi";
import { useTheme } from "@mui/material";
import PlanCard from "./PlanCard";
import { Skeleton } from "./subscriptionUi";
import usePublicPlans from "../../hooks/usePublicPlans";

// Public pricing page. Same cards as the in-app plan picker, fed by GET /plans.
// Custom-priced (contact sales) plans aren't listed. The trial's own limits aren't part of /plans,
// so the banner doesn't quote numbers; signed-in people see them in the app.
const PricingPage = () => {
  const theme = useTheme();
  const { plans: all, loading, error, retry } = usePublicPlans();
  const plans = all.filter((plan) => !plan.contact_sales);
  const signedIn = Boolean(localStorage.getItem("jwtToken"));

  const buttonClass =
    "flex h-10 w-full items-center justify-center rounded-lg px-3 text-center text-sm font-semibold text-white bg-accent-blue hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black";

  return (
    <main className="min-h-screen px-4 sm:px-8 py-10" style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-semibold">Plans &amp; pricing</h1>
        <p className="text-accent-gray-light mt-2 mb-6">Prices are per month.</p>

        {loading ? (
          <ul role="status" aria-label="Loading plans" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((item) => (
              <li key={item}>
                <Skeleton className="h-96 w-full" />
              </li>
            ))}
          </ul>
        ) : error ? (
          <div role="alert" className="p-4 border border-red-300 bg-red-50 text-red-900 rounded flex flex-wrap items-center justify-between gap-3">
            <span>We couldn't load the plans.</span>
            <button type="button" onClick={retry} className="font-semibold underline">
              Try again
            </button>
          </div>
        ) : plans.length === 0 ? (
          <p className="text-sm text-accent-gray-light">No plans are available right now. Please check back soon.</p>
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
            {plans.map((plan) => (
              <PlanCard
                key={plan.plan_id}
                plan={plan}
                action={
                  signedIn ? (
                    <Link to="/settings?tab=plan&upgrade=1" className={buttonClass}>
                      Choose {plan.name}
                    </Link>
                  ) : (
                    <Link to="/signup" className={buttonClass}>
                      Get started with {plan.name}
                    </Link>
                  )
                }
              />
            ))}
          </ul>
        )}

        {!signedIn && (
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-[#2252a5] p-5 text-white">
            <div className="flex items-center gap-4">
              <span className="hidden sm:flex h-11 w-11 items-center justify-center rounded-xl bg-white/15" aria-hidden="true">
                <FiClock className="h-5 w-5" />
              </span>
              <div>
                <p className="text-lg font-semibold">Every account starts with a free trial</p>
                <p className="text-sm text-white/90">Try it free, no card needed. Choose a plan when you're ready.</p>
              </div>
            </div>
            <Link to="/signup" className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#2252a5] hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
              Start free trial
            </Link>
          </div>
        )}
      </div>
    </main>
  );
};

export default PricingPage;
